
import { Payment } from '@/services/types';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { PaymentStatusBadge } from './PaymentStatusBadge';
import { Button } from '@/components/ui/button';
import { Download, ExternalLink, FileSymlink, Trash2 } from 'lucide-react';

export const getPaymentColumns = (
  onViewRental?: (rentalId: string) => void,
  onDownloadReceipt?: (payment: Payment) => void,
  onOpenInvoice?: (paymentId: string, paymentMethod?: string) => void,
  onDeletePayment?: (payment: Payment) => void
) => [
  {
    key: 'human_number',
    header: '№ платежа',
    cell: (payment: Payment) => {
      return payment.human_number ? (
        <span className="font-medium text-primary">{payment.human_number}</span>
      ) : (
        payment.id.substring(0, 8)
      );
    },
    sortable: true
  },
  {
    key: 'rentalId',
    header: '№ аренды',
    cell: (payment: Payment) => (
      <div className="flex items-center space-x-1">
        {payment.rentalHumanNumber ? (
          <span className="font-medium">{payment.rentalHumanNumber}</span>
        ) : (
          <span>{payment.rentalId.substring(0, 8)}</span>
        )}
        {onViewRental && payment.rentalId && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2"
            onClick={(e) => {
              e.stopPropagation();
              onViewRental(payment.rentalId);
            }}
            title="Детали аренды"
          >
            <FileSymlink className="h-3 w-3" />
          </Button>
        )}
      </div>
    ),
    sortable: true
  },
  {
    key: 'courierName',
    header: 'Клиент',
    cell: (payment: Payment) => payment.courierName || '—',
    sortable: true
  },
  {
    key: 'amount',
    header: 'Сумма',
    cell: (payment: Payment) => `${payment.amount} ₽`,
    sortable: true
  },
  {
    key: 'date',
    header: 'Дата',
    cell: (payment: Payment) => {
      try {
        return format(new Date(payment.date), 'dd.MM.yyyy HH:mm', { locale: ru });
      } catch (e) {
        return 'Некорректная дата';
      }
    },
    sortable: true
  },
  {
    key: 'method',
    header: 'Способ оплаты',
    cell: (payment: Payment) => {
      switch (payment.method) {
        case 'card': return 'Карта';
        case 'cash': return 'Наличные';
        case 'account': return 'Счет';
        case 'sbp': return 'СБП';
        default: return payment.method;
      }
    },
    sortable: true
  },
  {
    key: 'status',
    header: 'Статус',
    cell: (payment: Payment) => <PaymentStatusBadge status={payment.status} className="flex items-center" />,
    sortable: true
  },
  {
    key: 'actions',
    header: 'Действия',
    cell: (payment: Payment) => (
      <div className="flex space-x-2">
        {(payment.status === 'paid' || payment.status === 'completed') && onDownloadReceipt && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDownloadReceipt(payment);
            }}
            disabled={!payment.receiptUrl}
            title="Скачать чек"
          >
            <Download className="h-4 w-4" />
          </Button>
        )}
        {onOpenInvoice && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              // Для платежей с методом 'card' передаем payment.id вместо human_number
              onOpenInvoice(payment.method === 'card' ? payment.id : (payment.human_number || payment.id), payment.method);
            }}
            title="Открыть счет"
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        )}
        {onDeletePayment && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDeletePayment(payment);
            }}
            title="Удалить платеж"
            className="text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    ),
    sortable: false
  },
];
